import * as React from 'react';

import styled from '@emotion/styled';
import { Table, Tbody, Td as TdBase, Tr } from '@lightspeed/cirrus-table';
import { Box, Flex } from '@lightspeed/flame/Core';
import { IconHelp } from '@lightspeed/flame/Icon/Help';
import { Text, TextLink } from '@lightspeed/flame/Text';
import { Tooltip } from '@lightspeed/flame/Tooltip';
import Moment from 'moment';

import { getSettingsLink } from '../helpers/getSettingsLink';
import { fetchReorderSuggestion } from '../helpers/ReorderSuggestionAPI';
import { ToolTipTexts } from '../helpers/TooltipTexts';
import { createReactRenderer } from 'helpers/react-renderer';

import { ErrorState } from './errorState';
import { InitialState } from './initialState';

import type { ReorderSuggestions } from '../utils/types';

interface Props {
  itemID: number;
  hasAnalytics: boolean;
  qoh: number;
}

const { SuggestedLevelTooltipText, SuggestedNeedTooltipText, SuggestedPointTooltipText } =
  ToolTipTexts;

const Td = styled(TdBase)`
  &&& {
    font-weight: normal;
    color: #000;
    font-size: 13px;
    vertical-align: inherit;
    height: 25px;
    padding: 8px;
    border-left: 1px solid #d7dce1;
    &:first-of-type {
      border-left: none;
    }
  }
`;

const StyledTooltip = styled(Tooltip)`
  width: 300px;
  font-weight: normal;
  z-index: 10;
  font-size: 12px;
  line-height: 18px;
  text-align: left;
`;

const ShowDemandForecast: React.FC<Props> = ({ itemID, hasAnalytics, qoh }) => {
  const [data, setData] = React.useState<ReorderSuggestions>();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<Error>();
  const [browserLanguage, setBrowserLanguage] = React.useState(navigator.language || 'en');
  const duration =
    data && !error ? Moment(data.last_sync_time).locale(browserLanguage).fromNow() : '';

  const getLocalizeLanguage = () => {
    if (window.Localize) {
      setBrowserLanguage(window.Localize.getLanguage());
    }
  };

  React.useEffect(() => {
    getLocalizeLanguage();
  }, []);

  const showForecast = async () => {
    setIsLoading(true);
    await fetchReorderSuggestion({ itemID, setData, setError });
    setIsLoading(false);
    if (!error && data?.data && data?.data.length < 1) {
      setError({ name: 'Error', message: 'Server Data Error' });
    }
  };

  const DemandTable = () => {
    if (error || (data?.data && data?.data.length < 1)) {
      return <ErrorState onReload={showForecast} />;
    }
    if (data && data.data[0]) {
      const storeData = data.data[0];
      const { dynamic_reorder_point: reorderPoint, dynamic_reorder_level: reorderLevel } =
        storeData;
      return (
        <Table>
          <Tbody>
            <Tr>
              <Td width="20%" style={{ minWidth: '148px' }}>
                Suggested point{' '}
                <StyledTooltip content={SuggestedPointTooltipText}>
                  <IconHelp baseColor="none" detailsColor1="#232429" detailsColor2="#232429" />
                </StyledTooltip>
              </Td>
              <Td>
                <var>{reorderPoint}</var>
              </Td>
            </Tr>
            <Tr>
              <Td>
                Suggested level{' '}
                <StyledTooltip content={SuggestedLevelTooltipText}>
                  <IconHelp baseColor="none" detailsColor1="#232429" detailsColor2="#232429" />
                </StyledTooltip>
              </Td>
              <Td>
                <var>{reorderLevel}</var>
              </Td>
            </Tr>
            <Tr>
              <Td>
                Suggested need{' '}
                <StyledTooltip content={SuggestedNeedTooltipText}>
                  <IconHelp baseColor="none" detailsColor1="#232429" detailsColor2="#232429" />
                </StyledTooltip>
              </Td>
              <Td>
                <var>{qoh <= reorderPoint ? reorderLevel - qoh : 0}</var>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      );
    }
    return <ErrorState onReload={showForecast} />;
  };

  const DemandTableInitialState = () => {
    return (
      <InitialState hasAnalytics={hasAnalytics} showForecast={showForecast} isLoading={isLoading} />
    );
  };

  return (
    <Box
      style={{
        background: '#F5F7FF',
        borderBottom: '1px solid #eee',
        margin: '-3px -5px 0',
        fontSize: '12px',
      }}
    >
      <Flex justifyContent="space-between" style={{ background: '#D6DFFD', padding: '8px' }}>
        <Box>
          <Text
            style={{
              fontWeight: 'bold',
              color: '#000',
            }}
          >
            <img
              alt="Lightspeed logo"
              width="17"
              height="17"
              src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/smalltile.png"
              style={{ verticalAlign: 'text-bottom', marginRight: '6px' }}
            />
            Demand forecasting{' '}
            {duration && (
              <span style={{ color: '#020203', fontSize: '0.6rem', fontWeight: 'normal' }}>
                Last update <var>{duration}</var>
              </span>
            )}
          </Text>
        </Box>
        {data && (
          <Box style={{ textAlign: 'right', lineHeight: '1.4' }}>
            <TextLink
              href={getSettingsLink()}
              rel="noreferrer noopener nofollow"
              target="_blank"
              style={{ color: '#2E61DE', fontWeight: 'bold' }}
            >
              Edit Settings
            </TextLink>
          </Box>
        )}
      </Flex>
      {data || error ? <DemandTable /> : <DemandTableInitialState />}
    </Box>
  );
};

const RenderedComponent = createReactRenderer({
  component: ShowDemandForecast,
  selector: '[data-react-root="ShowDemandForecast"]',
});

export default ShowDemandForecast;

document.addEventListener('DOMContentLoaded', () => {
  if (window.lsCloud.Modules.hasModule('analytics.inventory.integration')) {
    history.onChangeState(
      [
        {
          name: ['item.views.item'],
          form_name: 'view',
          id: undefined,
        },
      ],
      () => {
        RenderedComponent.mount();
      },
      () => {
        RenderedComponent.unmount();
      }
    );
  }
});
